import { useMemo } from 'react'
import { Alert, Box, Card, CircularProgress } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartOptions,
  type ChartData,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { MetricState } from '../../common'
import { usePropertyClimateData, type PropertyResult } from '../../../../hooks/climateData'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'

type SeriesPoint = { x: number; y: number }

const detailChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
    tooltip: {
      callbacks: {
        label: ctx => {
          const label = ctx.dataset.label || ''
          const value = ctx.parsed.y as number
          if (ctx.dataset.yAxisID === 'days') {
            return `${label}: ${value.toFixed(0)} days / year`
          }
          return `${label}: ${value.toFixed(1)}`
        },
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: 'Year',
      },
    },
    score: {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Heat index (°C)',
      },
    },
    days: {
      type: 'linear',
      position: 'right',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Local hot days / year',
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
}

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

const getSeriesPoints = (metric: PropertyResult | undefined, tier: string): SeriesPoint[] => {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return []
  const tierData = metric.data?.data?.[tier]
  if (!tierData || !Array.isArray(tierData)) return []
  return (tierData as SeriesPoint[]).filter(point => typeof point?.x === 'number' && typeof point?.y === 'number')
}

type HeatStressTrendsProps = {
  propertyId: number
  emissionTier: string
}

function HeatStressTrends({ propertyId, emissionTier }: HeatStressTrendsProps) {
  const {
    data: heatScoresSeries,
    isLoading: heatScoresSeriesLoading,
    error: heatScoresSeriesError,
  } = usePropertyClimateData('hs:heat_scores', { propertyId }, { canonicalPeriods: null, aggParams: { by: [] } })
  const {
    data: heatDaysSeries,
    isLoading: heatDaysSeriesLoading,
    error: heatDaysSeriesError,
  } = usePropertyClimateData('hs:hot_days', { propertyId }, { canonicalPeriods: null, aggParams: { by: [] } })

  const heatScoreSeriesPoints = useMemo(
    () => getSeriesPoints(heatScoresSeries, emissionTier),
    [heatScoresSeries, emissionTier],
  )
  const hotDaysSeriesPoints = useMemo(
    () => getSeriesPoints(heatDaysSeries, emissionTier),
    [heatDaysSeries, emissionTier],
  )

  const labelYears = useMemo(() => {
    const years = new Set<number>()
    heatScoreSeriesPoints.forEach(point => years.add(point.x))
    hotDaysSeriesPoints.forEach(point => years.add(point.x))
    return Array.from(years).sort((a, b) => a - b)
  }, [heatScoreSeriesPoints, hotDaysSeriesPoints])

  const detailChartData = useMemo<ChartData<'line'>>(() => {
    const labels = labelYears.map(year => String(year))
    const datasets: ChartData<'line'>['datasets'] = []

    if (heatScoreSeriesPoints.length) {
      const map = new Map(heatScoreSeriesPoints.map(point => [point.x, point.y]))
      datasets.push({
        label: 'Heat index (°C)',
        data: labelYears.map(year => map.get(year) ?? Number.NaN),
        borderColor: '#d32f2f',
        backgroundColor: '#d32f2f',
        yAxisID: 'score',
        tension: 0.2,
        pointRadius: 2,
      })
    }

    if (hotDaysSeriesPoints.length) {
      const map = new Map(hotDaysSeriesPoints.map(point => [point.x, point.y]))
      datasets.push({
        label: 'Hot days / year',
        data: labelYears.map(year => map.get(year) ?? Number.NaN),
        borderColor: '#0288d1',
        backgroundColor: '#0288d1',
        borderDash: [6, 4],
        yAxisID: 'days',
        tension: 0.2,
        pointRadius: 2,
      })
    }

    return { labels, datasets }
  }, [labelYears, heatScoreSeriesPoints, hotDaysSeriesPoints])

  const detailHasData = (detailChartData.labels?.length ?? 0) > 0 && detailChartData.datasets.length > 0
  const detailLoading =
    ((heatScoresSeriesLoading && !heatScoresSeries) || (heatDaysSeriesLoading && !heatDaysSeries)) && !detailHasData
  const detailError = !detailHasData && heatScoresSeriesError != null && heatDaysSeriesError != null
  const partialDetailWarning =
    !detailHasData &&
    ((heatScoresSeriesError != null && !heatDaysSeriesError && !hotDaysSeriesPoints.length) ||
      (heatDaysSeriesError != null && !heatScoresSeriesError && !heatScoreSeriesPoints.length))

  return (
    <Card sx={{ p: 3 }}>
      <Box mb={2}>
        <ClimateSectionTitle
          title="Trends"
          subtitle="Annual projections for heat stress rating and hot days through the century"
          helpText="This chart shows the full available projection series for the selected emissions scenario. It is not filtered by the header horizon."
        />
      </Box>

      {partialDetailWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some heat-stress series could not be loaded. Showing the available data.
        </Alert>
      )}

      {detailLoading && (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!detailLoading && detailHasData && (
        <Box sx={{ height: 280 }}>
          <Line data={detailChartData} options={detailChartOptions} />
        </Box>
      )}

      {!detailLoading && !detailHasData && (
        <Alert severity={detailError ? 'error' : 'info'}>
          {detailError ? 'Unable to load detailed heat-stress data.' : 'No detailed heat-stress trends to display.'}
        </Alert>
      )}
    </Card>
  )
}

export default HeatStressTrends
